import React, { useState } from 'react';
import {
  Card,
  Text,
  Box,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconRoute,
  IconUsers,
  IconShare,
  IconLock,
  IconEye,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { TripDetail } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useLocaleDateFormatter } from '../../hooks/useLocaleDateFormatter';
import {
  OCCASION_COLORS,
  OCCASION_DEFAULT_COLORS,
  STATUS_COLORS,
} from '../../theme/tokens';
import {
  OCCASION_LABELS,
  STATUS_LABELS,
  VISIBILITY_LABELS,
  buildShareUrl,
} from '../../constants';
import { resolveDates } from '../../utils/trips/resolveDates';
import { isAdmin } from '../../utils/trips/isAdmin';
import ManageMembershipModal from './ManageMembershipModal';
export interface SummaryCardProps {
  readonly trip: TripDetail;
  readonly onEdit: (id: string) => void;
  readonly onDelete: (id: string) => void;
  readonly onItinerary?: (id: string) => void;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  trip,
  onEdit,
  onDelete,
  onItinerary,
}) => {
  const { user } = useAuth();
  const { formatDisplayDate } = useLocaleDateFormatter();
  const [managingTravelers, setManagingTravelers] = useState(false);

  const admin = isAdmin(trip, user?.id);
  const resolvedDates = resolveDates(trip);
  const primaryOccasion = trip.occasions[0];
  const occasionColors =
    (primaryOccasion && OCCASION_COLORS[primaryOccasion]) ||
    OCCASION_DEFAULT_COLORS;
  const statusColors = STATUS_COLORS[trip.status] ?? STATUS_COLORS.PLANNING;

  const formatDate = (dateString: string) =>
    formatDisplayDate(dateString, { fallbackText: '' });

  const stopsLabel =
    trip.derivedCities.length > 0
      ? `${trip.derivedCities.slice(0, 3).join(' → ')}${
          trip.derivedCities.length > 3
            ? ` +${trip.derivedCities.length - 3}`
            : ''
        }`
      : null;

  const budgetLabel =
    trip.budgetAmount != null && trip.budgetCurrency
      ? `${trip.budgetAmount.toLocaleString()} ${trip.budgetCurrency}`
      : null;

  const shareUrl = buildShareUrl(trip.shareToken);

  const handleCopyShareLink = async () => {
    if (!shareUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      notifications.show({
        title: 'Link copied',
        message: 'Share link copied to clipboard.',
        color: 'green',
        autoClose: 2000,
      });
    } catch {
      notifications.show({
        title: 'Copy failed',
        message: 'Could not copy link to clipboard.',
        color: 'red',
        autoClose: 2000,
      });
    }
  };

  const renderDateRow = () => {
    if (resolvedDates.source === 'none') {
      return (
        <Text size="sm" c="dimmed" fs="italic">
          No dates yet — add stops to your itinerary
        </Text>
      );
    }

    const startLabel = resolvedDates.start
      ? formatDate(resolvedDates.start)
      : '';
    const endLabel = resolvedDates.end ? formatDate(resolvedDates.end) : '';
    const nights =
      resolvedDates.source === 'itinerary' ? trip.totalNights : null;

    return (
      <Group gap={6} wrap="wrap" align="center">
        <Text size="sm" c="dimmed">
          {startLabel}
          {endLabel ? ` – ${endLabel}` : ''}
          {nights != null ? ` · ${nights}n` : ''}
          {resolvedDates.source === 'itinerary' ? ' (itinerary)' : ''}
        </Text>
        {resolvedDates.source === 'locked' && (
          <Badge
            size="xs"
            variant="filled"
            color="sand"
            leftSection={<IconLock size={10} />}
          >
            locked
          </Badge>
        )}
      </Group>
    );
  };

  return (
    <Card
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      data-cy="trip-card"
      style={{ overflow: 'hidden' }}
    >
      <Box
        h={42}
        px="sm"
        style={{
          backgroundColor: occasionColors.coverBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Group gap={6} wrap="nowrap">
          {trip.occasions.length === 0 ? (
            <Badge
              size="sm"
              variant="filled"
              styles={{
                root: {
                  backgroundColor: OCCASION_DEFAULT_COLORS.badgeFill,
                  color: OCCASION_DEFAULT_COLORS.badgeText,
                },
              }}
            >
              Trip
            </Badge>
          ) : (
            trip.occasions.map((id, index) => {
              const colors = OCCASION_COLORS[id] ?? OCCASION_DEFAULT_COLORS;
              return (
                <Badge
                  key={id}
                  size="sm"
                  variant="filled"
                  styles={{
                    root: {
                      backgroundColor: colors.badgeFill,
                      color: colors.badgeText,
                      opacity: index === 0 ? 1 : 0.5,
                      border:
                        index === 0
                          ? undefined
                          : '0.5px solid rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  {OCCASION_LABELS[id] ?? id}
                </Badge>
              );
            })
          )}
        </Group>
        <Badge
          size="sm"
          variant="filled"
          styles={{
            root: {
              backgroundColor: statusColors.bg,
              color: statusColors.text,
            },
          }}
        >
          {STATUS_LABELS[trip.status] ?? trip.status}
        </Badge>
      </Box>

      <Box p="md">
        <Text size="sm" fw={500} mb="xs">
          {trip.name}
        </Text>

        <Box mb="sm">{renderDateRow()}</Box>

        <Text
          size="sm"
          c={stopsLabel ? 'dimmed' : undefined}
          fs={stopsLabel ? undefined : 'italic'}
          mb="sm"
        >
          {stopsLabel ?? 'No stops yet'}
        </Text>

        <Group gap={6} mb="sm" wrap="wrap">
          <Group gap={4}>
            <IconEye size={14} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              {VISIBILITY_LABELS[trip.visibility] ?? trip.visibility}
            </Text>
          </Group>
          {budgetLabel && (
            <>
              <Text size="xs" c="dimmed">
                ·
              </Text>
              <Text size="xs" c="dimmed">
                {budgetLabel}
              </Text>
            </>
          )}
        </Group>

        <Group gap="xs" wrap="wrap" align="center" mb="md">
          {trip.memberships.slice(0, 4).map((membership, pidx) => (
            <Badge
              key={membership.traveler.id ?? pidx}
              variant="outline"
              size="sm"
            >
              {membership.traveler.name}
            </Badge>
          ))}
          {trip.memberships.length > 4 && (
            <Badge variant="outline" size="sm" color="gray">
              +{trip.memberships.length - 4}
            </Badge>
          )}
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            aria-label="Manage travelers"
            data-cy="manage-travelers"
            onClick={() => setManagingTravelers(true)}
          >
            <IconUsers size={15} />
          </ActionIcon>
        </Group>

        <Group justify="space-between">
          <Group gap="xs">
            {onItinerary && (
              <Tooltip label="Itinerary">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="teal"
                  aria-label="Trip itinerary"
                  data-cy="trip-itinerary"
                  onClick={() => onItinerary(trip.id.toString())}
                >
                  <IconRoute size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {admin && (
              <>
                <Tooltip label="Edit trip">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="blue"
                    aria-label="Edit trip"
                    data-cy="trip-edit"
                    onClick={() => onEdit(trip.id.toString())}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete trip">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    aria-label="Delete trip"
                    data-cy="trip-delete"
                    onClick={() => onDelete(trip.id.toString())}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
          {trip.shareLinkEnabled && trip.shareToken && (
            <Tooltip label="Copy share link">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label="Copy share link"
                data-cy="trip-share-copy"
                onClick={handleCopyShareLink}
              >
                <IconShare size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Box>

      <ManageMembershipModal
        opened={managingTravelers}
        onClose={() => setManagingTravelers(false)}
        trip={trip}
        currentUser={user}
      />
    </Card>
  );
};

export default SummaryCard;
